import type { RefObject } from 'react';
import { useEffect } from 'react';

import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Panel } from './Panel';
import { COMPONENTS_NAMESPACE } from '../../../../locales';
import { cn } from '../../../../utils/cn';
import { getColorClasses } from '../../../../utils/Colors';
import type { EventBehaviorProps } from '../../../../utils/EventBehavior';
import { useDropdownMenu } from '../../hooks/useDropdownMenu';
import { DROPDOWN_MAX_HEIGHT, DROPDOWN_SIZE } from '../../types';
import type { DropdownMenuOptionsProps } from '../../types';
import { Trigger } from '../shared/Trigger';

// Props type based on DropdownMenuOptionsProps, with open renamed to controlledOpen
// and searchPlaceholder/onSearch made always optional (instead of never in non-searchable mode)
type OptionsPickerProps<T> = Omit<
    DropdownMenuOptionsProps<T>,
    'open' | 'datePicker' | 'searchPlaceholder' | 'onSearch'
> &
    EventBehaviorProps & {
        controlledOpen?: boolean;
        searchPlaceholder?: string;
        onSearch?: (searchTerm: string) => void;
    };

export const OptionsPicker = <T extends string | number>({
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    badge,
    className,
    closeOnScroll = false,
    color,
    controlledOpen,
    disabled = false,
    error,
    eventPreventDefault,
    eventStopPropagation,
    formatSelectedOptions,
    hideCheckmarks = false,
    hideDivider = false,
    id,
    keepInitialPanelWidth = false,
    keepPlaceholder = false,
    matchTriggerWidth = false,
    maxHeight = DROPDOWN_MAX_HEIGHT,
    maxPanelWidth,
    minPanelWidth = 120,
    multiple = false,
    noOptionsText,
    onChange,
    onOpenChange,
    onSearch,
    optionAlign = 'left',
    optionClassName,
    optionElementType = 'button',
    options,
    panelClassName,
    placeholder,
    renderTrigger,
    roundedPanel = false,
    roundedTrigger = false,
    scrollable = true,
    searchable,
    searchPlaceholder,
    showBorder = true,
    size = DROPDOWN_SIZE.SM,
    triggerClassName,
    triggerWidth,
    value,
    ...props
}: OptionsPickerProps<T>) => {
    const { t } = useTranslation(COMPONENTS_NAMESPACE);

    const resolvedPlaceholder = placeholder ?? t('dropdownMenu.placeholder');
    const resolvedNoOptionsText = noOptionsText ?? t('dropdownMenu.noOptions');
    const resolvedSearchPlaceholder = searchPlaceholder ?? t('dropdownMenu.searchPlaceholder');
    const {
        containerRef,
        displayValue,
        effectiveColor,
        filteredOptions,
        handleSearchChange,
        handleSelect,
        handleToggle,
        initialPanelWidth,
        isOpen,
        isSelected,
        panelPosition,
        panelRef,
        searchTerm,
        setTriggerRef,
        triggerWidth: measuredTriggerWidth,
    } = useDropdownMenu({
        closeOnScroll,
        color,
        controlledOpen,
        formatSelectedOptions,
        keepInitialPanelWidth,
        keepPlaceholder,
        maxHeight,
        multiple,
        onChange,
        onOpenChange,
        onSearch,
        options,
        placeholder: resolvedPlaceholder,
        value,
    });

    const colorClasses = getColorClasses(effectiveColor);

    useEffect(() => {
        if (renderTrigger && containerRef.current) {
            const button = containerRef.current.querySelector('button, [role="button"]') as HTMLElement;
            if (button) {
                setTriggerRef(button);
            }
        }
    }, [renderTrigger, isOpen, containerRef, setTriggerRef]);

    return (
        <div
            className={cn('relative', className)}
            id={renderTrigger ? id : undefined}
            ref={containerRef as RefObject<HTMLDivElement>}
            {...props}
        >
            {renderTrigger ? (
                <div
                    onClick={handleToggle}
                    ref={(el) => {
                        if (el) {
                            const button = el.querySelector('button, [role="button"]') as HTMLElement;
                            setTriggerRef(button || el);
                        }
                    }}
                >
                    {renderTrigger({ isOpen, selectedValue: value })}
                </div>
            ) : (
                <Trigger
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                    id={id}
                    isOpen={isOpen}
                    displayValue={displayValue}
                    badge={badge}
                    colorClasses={colorClasses}
                    size={size}
                    roundedTrigger={roundedTrigger}
                    showBorder={showBorder}
                    error={Boolean(error)}
                    multiple={multiple}
                    triggerWidth={triggerWidth}
                    triggerClassName={triggerClassName}
                    hideDivider={hideDivider}
                    disabled={disabled}
                    onClick={handleToggle}
                    eventPreventDefault={eventPreventDefault}
                    eventStopPropagation={eventStopPropagation}
                />
            )}

            {error && <span className="text-[10px] leading-[1.46] text-alert-error mt-1 block">{error}</span>}

            {isOpen &&
                panelPosition &&
                createPortal(
                    <Panel
                        panelRef={panelRef}
                        panelPosition={panelPosition}
                        panelClassName={panelClassName}
                        roundedPanel={roundedPanel}
                        scrollable={scrollable}
                        searchable={searchable}
                        maxHeight={maxHeight}
                        maxPanelWidth={maxPanelWidth}
                        minPanelWidth={matchTriggerWidth && measuredTriggerWidth ? measuredTriggerWidth : minPanelWidth}
                        keepInitialPanelWidth={keepInitialPanelWidth}
                        initialPanelWidth={initialPanelWidth}
                        searchTerm={searchTerm}
                        searchPlaceholder={resolvedSearchPlaceholder}
                        onSearchChange={handleSearchChange}
                        filteredOptions={filteredOptions}
                        noOptionsText={resolvedNoOptionsText}
                        multiple={multiple}
                        hideCheckmarks={hideCheckmarks}
                        optionAlign={optionAlign}
                        optionClassName={optionClassName}
                        optionElementType={optionElementType}
                        isSelected={isSelected}
                        onSelect={handleSelect}
                    />,
                    document.body,
                )}
        </div>
    );
};
